import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { Mic, Square, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { TID } from "@/constants/testIds";
import { MicRecorderModals } from "@/components/add/MicRecorderModals";

export function MicRecorder({ autoStart = false, onDone }) {
  const { t, lang } = useI18n();
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [installment, setInstallment] = useState(null);
  const [earlyAi, setEarlyAi] = useState(null);
  const [commitment, setCommitment] = useState(null);
  const [quota, setQuota] = useState(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  useEffect(() => { if (autoStart) start(); api.get("/ai/voice/quota").then(({ data }) => setQuota(data)).catch(() => {}); return () => cleanup(); /* eslint-disable-next-line */ }, []);

  const cleanup = () => {
    const rec = mediaRef.current;
    if (!rec) return;
    try { rec.onstop = null; rec.ondataavailable = null; } catch { /* ignore */ }
    try { if (rec.state !== "inactive") rec.stop(); } catch { /* ignore */ }
    try { rec.stream?.getTracks?.().forEach((tr) => tr.stop()); } catch { /* ignore */ }
    mediaRef.current = null;
  };

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size) chunksRef.current.push(e.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        await upload(new Blob(chunksRef.current, { type: mime || "audio/webm" }));
      };
      rec.start(); mediaRef.current = rec; setRecording(true);
    } catch { toast.error(t("micNotSupported")); }
  };

  const stop = () => { mediaRef.current?.stop(); setRecording(false); };

  const upload = async (blob) => {
    setBusy(true);
    const fd = new FormData();
    fd.append("audio", blob, "voice.webm");
    try {
      const { data } = await api.post("/ai/voice?preview=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.quota) setQuota(data.quota);
      if (data.installment_preview) { setInstallment({ transcript: data.transcript, preview: data.installment_preview }); return; }
      if (data.early_payment_preview) { setEarlyAi({ transcript: data.transcript, previewData: data.early_payment_preview }); return; }
      if (data.commitment_preview) { setCommitment({ transcript: data.transcript, preview: data.commitment_preview }); return; }
      const items = data.extracted || [];
      if (items.length === 0) toast.info(data.transcript ? `"${data.transcript}" — ${t("error")}` : t("error"));
      else setPreview({ transcript: data.transcript, extracted: items });
    } catch (e) {
      const r = e?.response;
      if (r?.status === 429) { const q = typeof r.data?.detail === "object" ? r.data.detail : r.data; setQuota({ ...q, allowed: false }); toast.error(lang === "ar" ? `تجاوزت الحد اليومي (${q.limit} تسجيل). يعود غداً.` : `Daily limit reached (${q.limit} recordings). Resets tomorrow.`); }
      else toast.error(e?.response?.data?.detail || t("error"));
    } finally { setBusy(false); }
  };

  return (
    <div className="flex flex-col items-center gap-6">
      <button
        data-testid={TID.micButton}
        onClick={recording ? stop : start}
        disabled={busy}
        className={`relative w-28 h-28 rounded-full grid place-items-center text-white transition-all active:scale-95 ${recording ? "bg-red-600 mic-pulse" : "neu-fill-blue shadow-xl shadow-blue-900/30 hover:opacity-90"}`}
      >
        {busy ? <Loader2 className="w-10 h-10 animate-spin" /> : recording ? <Square className="w-10 h-10 fill-white" /> : <Mic className="w-10 h-10" />}
      </button>
      <p className="text-sm text-slate-600 text-center max-w-sm">
        {busy ? t("processing") : recording ? t("listening") : t("hold")}
      </p>
      {quota && !quota.unlimited && (
        <p data-testid="voice-quota" className={`text-[11px] font-bold tabular-nums ${quota.allowed ? "text-slate-500" : "text-red-500"}`}>
          {lang === "ar" ? `المتبقي اليوم: ${quota.remaining}/${quota.limit}` : `Today: ${quota.remaining}/${quota.limit} left`}
        </p>
      )}
      <MicRecorderModals preview={preview} installment={installment} earlyAi={earlyAi} commitment={commitment}
        close={(k) => { const s = { preview: setPreview, installment: setInstallment, earlyAi: setEarlyAi, commitment: setCommitment }[k]; s?.(null); onDone?.(); }} />
    </div>);
}
